// @owners { team: patient-health }
import { SPACING } from '@alto/design-library-tokens';
import { H2, LgPadding, ListBase, MdSpacing } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { useNavigation } from '@alto/navigation';
import { isAfter, isValid, parseISO, subMonths } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import { ASSISTANT_PRESSED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import { ASSISTANT_CATEGORIES, type AssistantShortcut } from '~shared/features/alto-assistant/constants';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { getPastShipments } from '~shared/selectors/alto-assistant/getRecentShipments';
import { useSelectorShared } from '~shared/store';
import { FAQ_KEYS } from '../faq/Constants';
import { MenuOption } from '../wizard/MenuOption';

const THREE_MONTHS_AGO = subMonths(new Date(), 3);

export const Shortcuts = () => {
  const { navigate } = useNavigation();
  const { trackEvent } = useAnalytics();

  const user = useSelectorShared(getCurrentUser);
  const pastShipments = useSelectorShared(getPastShipments);

  const { value: showNewSupportPage } = Experimentation.useFeatureFlag('altoassistant_support_page');

  const trackShortcutPress = useCallback(
    (action: AssistantShortcut) => {
      trackEvent({
        event: EVENTS.ASSISTANT_COMPONENT_PRESSED,
        params: {
          name: ASSISTANT_PRESSED_EVENTS.SHORTCUT,
          action,
          origin: ORIGIN_NAMES.MESSAGES,
        },
      });
    },
    [trackEvent],
  );

  const hasSharpsContainerInRecentShipments = useMemo(() => {
    return (
      pastShipments.filter(({ delivered_at, includes_sharps_container }) => {
        const parsedDeliveredAt = parseISO(delivered_at ?? '');
        return isValid(parsedDeliveredAt) && isAfter(parsedDeliveredAt, THREE_MONTHS_AGO) && includes_sharps_container;
      }).length > 0
    );
  }, [pastShipments]);

  const oldLandingPageShortcuts = useMemo(
    () => [
      {
        key: 'download-receipts',
        component: (
          <MenuOption
            iconName="receipt-duo"
            text="Download receipts"
            onPress={() => {
              trackShortcutPress('download_receipts');
              navigate('RoutePayments');
            }}
          />
        ),
      },
      {
        key: 'edit-insurance',
        component: (
          <MenuOption
            iconName="idcard-duo"
            text="Edit insurance"
            onPress={() => {
              trackShortcutPress('edit_insurance');
              if (!user) return;
              navigate('RouteUserProfile', { userID: user.id });
            }}
          />
        ),
      },
      {
        key: 'sharps-container-disposal',
        component: (
          <MenuOption
            iconName="trash-duo"
            text="Sharps container disposal"
            onPress={() => {
              trackShortcutPress('sharps_disposal_container');
              navigate('RouteFaq', { faqKey: FAQ_KEYS.SHARPS_CONTAINER_DISPOSAL, buttonLabelDone: 'Done' });
            }}
            hide={!hasSharpsContainerInRecentShipments}
          />
        ),
      },
      {
        key: 'transfer-prescription-to-alto',
        component: (
          <MenuOption
            iconName="swap-duo"
            text="Transfer prescription to Alto"
            onPress={() => {
              trackShortcutPress('transfer_in');
              navigate('RouteTransferPrescription', { origin: ORIGIN_NAMES.MESSAGES });
            }}
          />
        ),
      },
      {
        key: 'edit-an-upcoming-delivery',
        component: (
          <MenuOption
            iconName="box-duo"
            text="Edit an upcoming delivery"
            onPress={() => {
              trackShortcutPress('edit_upcoming_delivery');
              navigate('RouteMessagesDeliverySelection', {
                origin: ORIGIN_NAMES.MESSAGES,
                category: ASSISTANT_CATEGORIES.DELIVERIES,
              });
            }}
          />
        ),
      },
      {
        key: 'frequently-asked-questions',
        component: (
          <MenuOption
            iconName="questionmark-duo"
            text="Frequently asked questions"
            onPress={() => {
              trackShortcutPress('faq');
              navigate('RouteAccountFaq', { origin: ORIGIN_NAMES.MESSAGES });
            }}
          />
        ),
      },
    ],
    [hasSharpsContainerInRecentShipments, navigate, trackShortcutPress, user],
  );

  const newSupportPageShortcuts = useMemo(
    () => [
      {
        key: 'edit-insurance',
        component: (
          <MenuOption
            iconName="idcard-duo"
            text="Edit insurance"
            onPress={() => {
              trackShortcutPress('edit_insurance');
              if (!user) return;
              navigate('RouteUserProfile', { userID: user.id });
            }}
          />
        ),
      },
      {
        key: 'edit-an-upcoming-delivery',
        component: (
          <MenuOption
            iconName="box-duo"
            text="Edit an upcoming delivery"
            onPress={() => {
              trackShortcutPress('edit_upcoming_delivery');
              navigate('RouteMessagesDeliverySelection', {
                origin: ORIGIN_NAMES.MESSAGES,
                category: ASSISTANT_CATEGORIES.DELIVERIES,
              });
            }}
          />
        ),
      },
      {
        key: 'transfer-prescription-to-alto',
        component: (
          <MenuOption
            iconName="swap-duo"
            text="Transfer prescription to Alto"
            onPress={() => {
              trackShortcutPress('transfer_in');
              navigate('RouteTransferPrescription', { origin: ORIGIN_NAMES.MESSAGES });
            }}
          />
        ),
      },
      {
        key: 'download-receipts',
        component: (
          <MenuOption
            iconName="receipt-duo"
            text="Download receipts"
            onPress={() => {
              trackShortcutPress('download_receipts');
              navigate('RoutePayments');
            }}
          />
        ),
      },

      {
        key: 'frequently-asked-questions',
        component: (
          <MenuOption
            iconName="questionmark-duo"
            text="Frequently asked questions"
            onPress={() => {
              trackShortcutPress('faq');
              navigate('RouteAccountFaq', { origin: ORIGIN_NAMES.MESSAGES });
            }}
          />
        ),
      },
    ],
    [navigate, trackShortcutPress, user],
  );

  // TODO: Wrap in Card once experiment is complete and we move to new SupportPage
  return (
    <>
      <LgPadding
        topPadding={SPACING.STATIC.NONE}
        bottomPadding={SPACING.STATIC.NONE}
      >
        <H2>Shortcuts</H2>
      </LgPadding>
      <MdSpacing />
      <ListBase items={showNewSupportPage ? newSupportPageShortcuts : oldLandingPageShortcuts} />
    </>
  );
};
